import { useCallback, type JSX } from "react"
import type { RendererProps } from "../core/element-registry"
import { RenderContext, useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { ElementRenderer, buildIteratedResolveSymbol, liftV4Props } from "./element-renderer"
import {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableRow,
  TableHead,
  TableCell,
} from "../components/ui/table"
import { cn } from "../lib/utils"

// JITRULE follow-up (fixture-render-debug, 2026-08-07) — `className` is an
// established expression-bearing position elsewhere in this renderer
// (element-renderer.tsx: `ElementRenderer`'s own top-of-function `#{...}`
// resolution, and `IteratedElement`'s per-item resolution) — resolved
// through the SAME `resolveSymbol` used for `.text`. This file's row/cell
// `className` was never routed through `resolveSymbol` at all, so a
// `#{...}` expression (e.g. a selected-row highlight ternary) rendered as
// literal, un-evaluated source text in the `class` attribute. General rule,
// no field/element-type name involved: any `className` containing `#{`
// gets resolved; anything else (including `undefined`) passes through
// unchanged.
function resolveClassNameExpr(
  className: string | undefined,
  resolveSymbol: (expr: string) => unknown,
): string | undefined {
  if (!className?.includes("#{")) return className
  const resolved = resolveSymbol(className)
  return typeof resolved === "string" ? resolved : className
}

// JITRULE follow-up (fixture-render-debug, 2026-08-07) — this file's
// compound-table path (`type: "table"` with table-header/table-body/
// table-footer children) never dispatches those children through
// `ElementRenderer` — it walks the JSON tree directly below
// (`renderHeaderRows`/`renderDataRows`) so a single `<table>` skeleton can
// host N rows without the generic per-item wrapper `<div>` a `<tr>` can't
// legally contain (the same HTML content-model constraint RENDER-PARITY-1
// follow-up 2/699f605f9 solved for the OTHER table shape, in
// table-structure-renderers.tsx, where table-header/table-body/table-footer
// reach the element registry as standalone elements). Because this file's
// walk never reaches that registry, a section's own `iterateOver`/`itemVar`
// was silently ignored — the old `renderDataRows` cloned each entry in
// `rows` (the AUTHORED ROW TEMPLATES) once, never resolving the bound
// collection, so N items rendered as exactly 1 row.
//
// `withSectionIteration` is the general, type-level fix (no screen/block/
// field/app name, no element-type enumeration): ANY table-header/
// table-body/table-footer section node with `iterateOver` set gets its
// collection resolved and each row template repeated once per item —
// cross-producting row templates × items exactly like
// `TableSectionStructureRenderer` (table-structure-renderers.tsx) already
// does for its own reachable shape, so the two table renderers agree on
// this shape. Each repetition is wrapped in a `RenderContext.Provider`
// carrying an item-scoped `resolveSymbol`, built via the SAME
// `buildIteratedResolveSymbol` helper 699f605f9 extracted for exactly this
// purpose — so a cell's own text/className AND any element nested in a cell
// (e.g. a badge reading `#{item.x}` via its own `useRenderContext()`) both
// resolve against the current item. A section without `iterateOver` renders
// its row templates once each, unchanged from prior behavior.
function withSectionIteration(
  sectionNode: RendererProps["element"] | undefined,
  rowTemplates: RendererProps["element"][],
  parentCtx: ReturnType<typeof useRenderContext>,
  renderOneRow: (
    row: RendererProps["element"],
    resolveSymbol: (expr: string) => unknown,
    rowIndex: number,
  ) => JSX.Element,
): JSX.Element[] {
  if (!sectionNode?.iterateOver) {
    return rowTemplates.map((row, rowIndex) => renderOneRow(row, parentCtx.resolveSymbol, rowIndex))
  }

  const rawItems = parentCtx.resolveSymbol(sectionNode.iterateOver)
  const items = Array.isArray(rawItems) ? rawItems : []
  return items.flatMap((item, i) => {
    const itemResolveSymbol = buildIteratedResolveSymbol(item, sectionNode.itemVar, i, parentCtx)
    const iterationIdentitySuffix = `${parentCtx.iterationIdentitySuffix ?? ""}__iter_${i}`
    const itemCtx: ReturnType<typeof useRenderContext> = {
      ...parentCtx,
      itemContext: {
        ...(parentCtx.itemContext ?? {}),
        ...(sectionNode.itemVar ? { [sectionNode.itemVar]: item } : {}),
        _index: i,
      },
      resolveSymbol: itemResolveSymbol,
      iterationIdentitySuffix,
    }
    return rowTemplates.map((row, rowIdx) => (
      <RenderContext.Provider key={`${row.id}-${rowIdx}-${i}`} value={itemCtx}>
        {renderOneRow(row, itemResolveSymbol, i)}
      </RenderContext.Provider>
    ))
  })
}

interface KeyedColumn {
  key: string
  label?: string
  className?: string
}

interface KeyedRow {
  id?: string
  className?: string
  cells?: Record<string, unknown>
}

function KeyedCellsTable({
  element,
  columns,
  rows,
  resolvedVariant,
  resolvedSize,
  isStriped,
  resolveSymbol,
  onRowClick,
}: {
  element: RendererProps["element"]
  columns: KeyedColumn[]
  rows: KeyedRow[]
  resolvedVariant: string
  resolvedSize: string
  isStriped: boolean
  resolveSymbol: (expr: string) => unknown
  onRowClick: (rowIndex: number) => void
}) {
  const renderCell = (value: unknown, key: string) => {
    if (value === null || value === undefined) return null
    if (typeof value === "string" || typeof value === "number") {
      return String(resolveSymbol(String(value)) ?? value)
    }
    if (typeof value === "object" && typeof (value as { type?: unknown }).type === "string") {
      const cellElement = value as RendererProps["element"]
      return <ElementRenderer key={cellElement.id ?? key} element={cellElement} />
    }
    return null
  }

  return (
    <div
      className={cn("cn-table-container", element.className)}
      data-testid={`table-${element.id}`}
      data-atom-type="table"
      data-variant={resolvedVariant}
      data-size={resolvedSize}
    >
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map((col) => (
              <TableHead key={col.key} className={col.className} data-column={col.label ?? col.key}>
                {col.label !== undefined ? String(resolveSymbol(col.label) ?? col.label) : col.key}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, rowIndex) => (
            <TableRow
              key={row.id ?? rowIndex}
              className={cn(row.className, isStriped && rowIndex % 2 === 1 && "bg-muted/50")}
              data-row-index={rowIndex}
              onClick={() => onRowClick(rowIndex)}
            >
              {columns.map((col) => (
                <TableCell key={col.key} className={col.className} data-column={col.label ?? col.key}>
                  {renderCell(row.cells?.[col.key], `${col.key}-${rowIndex}`)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

export function TableRenderer({ element }: RendererProps) {
  const ctx = useRenderContext()
  const { resolveSymbol, dispatchEvent } = ctx
  // Theme-v2 atom defaults feed the fallback chain:
  //   element.variant/size → atomDefaults.default* → shadcn "default"
  // Note: `element.variant === "striped"` is a legacy row-banding flag used
  // by the body row className below; it coexists with the theme-variant
  // data-* surface. The raw token is preserved on data-variant.
  const atomDefaults = useAtomDefaults("table")
  const resolvedVariant = (element.variant as string | undefined)
    ?? atomDefaults.defaultVariant
    ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"
  const isStriped = element.variant === "striped"

  const handleRowClick = useCallback(
    (rowIndex: number) => {
      dispatchEvent(element.id, "rowClick", { rowIndex })
    },
    [element.id, dispatchEvent],
  )

  // JITRULE Task 2: header/body/footer/row/cell elements below all trace
  // back to this array — lifting once here covers headerNode/bodyNodes/
  // footerNode's own fields (className, etc.) too.
  const children = (element.children ?? []).map(liftV4Props)

  // Keyed-cells shape (generator-emitted; R1 grading found the corpus block
  // data-display-dashboard-table carried ALL its data this way yet rendered
  // an empty body): `columns: [{key,label,className}]` +
  // `rows: [{cells: {colKey: element|text}}]`. Additive — only used when no
  // children are present, so both children-based shapes are untouched.
  const keyedColumns = (element as { columns?: unknown }).columns
  const keyedRows = (element as { rows?: unknown }).rows
  if (
    children.length === 0 &&
    Array.isArray(keyedColumns) &&
    keyedColumns.length > 0 &&
    Array.isArray(keyedRows)
  ) {
    return (
      <KeyedCellsTable
        element={element}
        columns={keyedColumns as KeyedColumn[]}
        rows={keyedRows as KeyedRow[]}
        resolvedVariant={resolvedVariant}
        resolvedSize={resolvedSize}
        isStriped={isStriped}
        resolveSymbol={resolveSymbol}
        onRowClick={handleRowClick}
      />
    )
  }

  // Support both flat (old: [headerRow, ...dataRows]) and compound semantic
  // tables (new: [table-header, table-body, table-footer]). Compound sections
  // are selected by type instead of position so authoring order cannot turn a
  // <tbody>/<tfoot> into a synthetic header row. HTML allows multiple tbody
  // sections, so preserve each authored section and all of its rows.
  const headerNode = children.find((child) => child.type === "table-header")
  const bodyNodes = children.filter((child) => child.type === "table-body")
  const footerNode = children.find((child) => child.type === "table-footer")
  const isCompound = headerNode !== undefined
    || bodyNodes.length > 0
    || footerNode !== undefined

  const flatHeaderRow = !isCompound ? children[0] : undefined
  const headerRows = isCompound
    ? (headerNode?.children ?? [])
    : (flatHeaderRow ? [flatHeaderRow] : [])
  const flatDataRows = !isCompound ? children.slice(1) : []

  // Native table sections and rows collapse to zero height when authored with
  // an empty children array, even when their className carries min-height and
  // padding. Keep the semantic empty node in the table and expose its authored
  // layout on a sibling logical host so it remains measurable without inserting
  // synthetic rows or cells into the document structure.
  const emptyStructureNodes: Array<typeof children[number]> = [
    ...(headerNode && (headerNode.children?.length ?? 0) === 0 ? [headerNode] : []),
    ...bodyNodes.filter((body) => (body.children?.length ?? 0) === 0),
    ...(footerNode && (footerNode.children?.length ?? 0) === 0 ? [footerNode] : []),
    ...headerRows.filter((row) => (row.children?.length ?? 0) === 0),
    ...bodyNodes.flatMap((body) => (
      body.children ?? []
    ).filter((row) => (row.children?.length ?? 0) === 0)),
    ...(footerNode?.children ?? []).filter((row) => (row.children?.length ?? 0) === 0),
  ]

  // Collect header texts for data-column attributes on body/footer cells.
  // The first header row retains the legacy column-label behaviour.
  // JITRULE Task 2: row/cell elements may nest their fields under `props`
  // (same defect class fixed in the sibling table-structure-renderers.tsx
  // today — this file is the OTHER table shape, plain `type: "table"` with
  // header/body/footer children, not yet covered by that fix).
  const headerTexts = headerRows[0]?.children?.map(liftV4Props).map((cell) =>
    cell.text !== undefined
      ? String(resolveSymbol(String(cell.text)) ?? "")
      : "",
  ) ?? []

  const renderCellContent = (
    rawCell: typeof children[number],
    resolveSymbolForCell: (expr: string) => unknown,
  ) => {
    const cell = liftV4Props(rawCell)
    const text = cell.text !== undefined
      ? String(resolveSymbolForCell(String(cell.text)) ?? "")
      : undefined
    return (
      <>
        {text}
        {cell.children?.map((child) => (
          <ElementRenderer key={child.id} element={child} />
        ))}
      </>
    )
  }

  const renderHeaderRows = (
    sectionNode: typeof children[number] | undefined,
    rows: typeof children,
  ) => withSectionIteration(sectionNode, rows.map(liftV4Props), ctx, (row, resolveSymbolForRow, rowIndex) => (
    <TableRow
      key={`${row.id}-${rowIndex}`}
      className={resolveClassNameExpr(row.className, resolveSymbolForRow)}
      data-cogo-element-id={row.id}
      data-cogo-type={row.type}
    >
      {row.children?.map(liftV4Props).map((cell) => {
        const text = cell.text !== undefined
          ? String(resolveSymbolForRow(String(cell.text)) ?? "")
          : ""

        return (
          <TableHead
            key={cell.id}
            className={resolveClassNameExpr(cell.className, resolveSymbolForRow)}
            data-column={text}
            data-cogo-element-id={cell.id}
            data-cogo-type={cell.type}
            align={cell.align}
            colSpan={typeof cell.colSpan === "number" ? cell.colSpan : undefined}
            style={cell.align ? { textAlign: cell.align } : undefined}
          >
            {renderCellContent(cell, resolveSymbolForRow)}
          </TableHead>
        )
      })}
    </TableRow>
  ))

  const renderDataRows = (
    sectionNode: typeof children[number] | undefined,
    rows: typeof children,
    { interactive, striped }: { interactive: boolean; striped: boolean },
  ) => withSectionIteration(sectionNode, rows.map(liftV4Props), ctx, (row, resolveSymbolForRow, rowIndex) => (
    <TableRow
      key={`${row.id}-${rowIndex}`}
      className={cn(
        resolveClassNameExpr(row.className, resolveSymbolForRow),
        striped && isStriped && rowIndex % 2 === 1 && "bg-muted/50",
      )}
      data-cogo-element-id={row.id}
      data-cogo-type={row.type}
      onClick={interactive ? () => handleRowClick(rowIndex) : undefined}
    >
      {row.children?.map(liftV4Props).map((cell, cellIndex) => (
        <TableCell
          key={cell.id}
          className={resolveClassNameExpr(cell.className, resolveSymbolForRow)}
          data-column={headerTexts[cellIndex] ?? ""}
          data-row-index={interactive ? rowIndex : undefined}
          data-cogo-element-id={cell.id}
          data-cogo-type={cell.type}
          align={cell.align}
          colSpan={typeof cell.colSpan === "number" ? cell.colSpan : undefined}
          style={cell.align ? { textAlign: cell.align } : undefined}
        >
          {renderCellContent(cell, resolveSymbolForRow)}
        </TableCell>
      ))}
    </TableRow>
  ))

  return (
    <div
      className={cn("cn-table-container", element.className)}
      data-testid={`table-${element.id}`}
      data-atom-type="table"
      data-variant={resolvedVariant}
      data-size={resolvedSize}
    >
      {emptyStructureNodes.map(liftV4Props).map((node) => (
        <div
          key={`${node.type}-${node.id}`}
          className={node.className}
          data-cogo-element-id={node.id}
          data-cogo-type={node.type}
          data-table-empty-structure-host="true"
        />
      ))}
      <Table>
        {(headerNode !== undefined || headerRows.length > 0) && (
          <TableHeader
            className={headerNode?.className}
            data-cogo-element-id={headerNode?.id}
            data-cogo-type={headerNode?.type}
          >
            {renderHeaderRows(headerNode, headerRows)}
          </TableHeader>
        )}
        {isCompound ? (
          bodyNodes.map((bodyNode) => (
            <TableBody
              key={bodyNode.id}
              className={bodyNode.className}
              data-cogo-element-id={bodyNode.id}
              data-cogo-type={bodyNode.type}
            >
              {renderDataRows(bodyNode, bodyNode.children ?? [], { interactive: true, striped: true })}
            </TableBody>
          ))
        ) : (
          <TableBody>
            {renderDataRows(undefined, flatDataRows, { interactive: true, striped: true })}
          </TableBody>
        )}
        {footerNode && (
          <TableFooter
            className={footerNode.className}
            data-cogo-element-id={footerNode.id}
            data-cogo-type={footerNode.type}
          >
            {renderDataRows(footerNode, footerNode.children ?? [], { interactive: false, striped: false })}
          </TableFooter>
        )}
      </Table>
    </div>
  )
}
